import * as React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { actionCreators as modelActionCreators } from '../../store/Model'
import { actionCreators as optimActionCreators } from '../../store/Optim'
import { actionCreators as GlobalStoreCreator } from '../../store/General'
import { actionCreators as fordForecastActionCreators } from '../../store/FordForecast'
import { ToggleSwitch } from '../common/ToggleSwitch'
import { ContinBox } from './att-editor/ContinBox'
import './AttributeBox.scss'

export function AttributeBoxFordU833({
  prod,
  att,
  configModel,
  allowOptimize,
  monitor_atts,
}) {
  const models = useSelector((state) => state.model)
  const dispatch = useDispatch()

  let model = models[configModel]
  let prodDef_atts = model?.config?.productDef?.atts

  if (!model || !prod || !att || !prodDef_atts) return null

  const play = model.config?.play || []
  const base = model.config?.base || []

  // Find the current product objects safely (play and base are arrays of products)
  const currentPlay = play.find((p) => p.uid === prod.uid) || {}
  const currentBase = base.find((p) => p.uid === prod.uid) || {}

  let dirty = false
  let error = null

  const isAttLocked = false // Optimization locking not active in this version
  let content = null

  const setPlay = async (prodId, attname, level_val) => {
    // Project-specific logic for setting play values (dispatches only to base model)
    // Note: The dynamic play_config building is preserved but unused in dispatch
    const att_name = attname.split('_')[0]
    const util_level_match = attname.match(/_(\d+)$/)
    const util_level = util_level_match
      ? parseInt(util_level_match[1], 10)
      : null
    const isModel2 = [
      'utility',
      'premium',
      'cold',
      'accessory',
      'software',
      'charging',
    ].includes(att_name)
    const utility_name = util_level ? `${att_name}${util_level}` : null
    const modelname =
      isModel2 && utility_name
        ? `model_fordu833_${utility_name}`
        : `model_fordu833_${att_name}`
    const subModel = models[modelname]

    const subProdDef_atts = subModel?.config?.productDef?.atts
    const num_prods = subModel?.modelInfo?.numProdsOnFly

    const originalPlay =
      { ...models['model_fordu833_base']?.config.play[0] } || {}
    const play_config = []

    if (subProdDef_atts && num_prods) {
      for (let i = 1; i <= num_prods; i++) {
        const levelLabel =
          isModel2 && util_level
            ? subProdDef_atts
                .find((a) => a.name === att_name)
                ?.levels.find((l) => l.value == util_level)?.label
            : subProdDef_atts.find((a) => a.name === att_name)?.levels[i - 1]
                ?.label

        const productConfig = {
          uid: `prod${i}`,
          label: levelLabel || null,
          available: 1,
          base_01: attname === 'base_01' ? level_val : originalPlay.base_01,
          [`${att_name}`]: isModel2 ? util_level : i,
        }

        const priceAttName = isModel2
          ? util_level >= 10
            ? `${att_name}_${util_level}`
            : `${att_name}_0${util_level}`
          : i >= 10
          ? `${att_name}_${i}`
          : `${att_name}_0${i}`
        const priceAtt = subProdDef_atts.find((a) => a.name === priceAttName)
        if (priceAtt && priceAtt.levels?.length > 1) {
          productConfig[priceAttName] = attname.includes(
            isModel2 ? util_level.toString() : i.toString()
          )
            ? level_val
            : originalPlay[priceAttName]
        }
        play_config.push(productConfig)
      }
    }

    // Dispatch only to the base model (project-specific behavior)
    await dispatch(
      modelActionCreators.setPlay(
        `model_fordu833_base`,
        prodId,
        attname,
        level_val
      )
    )
  }

  const setLevelAllow = (prodId, attname, level_val, allowed) => {
    dispatch(
      optimActionCreators.setLevelAllow(prodId, attname, level_val, allowed)
    )
    dispatch(GlobalStoreCreator.setGlobalStore('forecast_needed', true))
  }

  if (att.type === 'discrete') {
    const playValue = currentPlay[att.name] ?? att.defaultValue
    const baseValue = currentBase[att.name] ?? att.defaultValue

    if (playValue !== baseValue) {
      dirty = true
    }

    content = att.levels.map((level) => {
      const isLevelAllowed =
        att.levels.find((l) => l.value === level.value)?.allowed ?? true

      // Price attribute for this specific level (e.g., cab_01 or accessory_10)
      // Corrected regex matching to handle multi-digit indices properly
      let priceAttName = `${att.name}_0${level.value}`
      if (level.value >= 10) {
        priceAttName = `${att.name}_${level.value}`
      }

      // Fallback/Legacy format check just in case
      let priceAttName2 = `${att.name}_0${level.value}`
      if (level.value >= 10) {
        priceAttName2 = `${att.name}_${level.value}`
      }

      const priceAtt = prodDef_atts.find(
        (a) => a.name === priceAttName || a.name === priceAttName2
      )
      const priceLevels = priceAtt?.levels || []

      let priceContent = null
      let sel_price = null

      if (priceAtt && priceLevels.length > 0) {
        const playPrice = currentPlay[priceAtt.name] ?? priceAtt.defaultValue
        const basePrice = currentBase[priceAtt.name] ?? priceAtt.defaultValue

        if (playPrice !== basePrice) {
          dirty = true
        }

        sel_price =
          priceLevels.find((pl) => pl.value === playPrice) || priceLevels[0]

        priceContent = (
          <div
            className="price-boxes"
            style={{
              gridTemplateColumns: `repeat(${Math.max(
                priceLevels.length,
                5
              )}, 1fr)`,
              display: 'grid',
              width: '50%',
            }}
          >
            {priceLevels.map((priceLevel, index) => (
              <div
                key={index}
                className={`price-box ${
                  sel_price?.value === priceLevel.value ? 'sel' : ''
                }`}
                onClick={() =>
                  setPlay(prod.uid, priceAtt.name, priceLevel.value)
                }
              >
                {priceLevel.label}
              </div>
            ))}
          </div>
        )
      }

      return !allowOptimize || isAttLocked ? (
        <div key={`${att.name}:${level.value}`} className="att-level-row">
          <div
            className={`att-level-price ${
              level.value === playValue ? 'sel' : ''
            }`}
            title={level.label}
          >
            {level.label}
          </div>
          {priceContent}
        </div>
      ) : (
        <div
          key={`${att.name}:${level.value}`}
          className={`opt-att-level with-toggle${isLevelAllowed ? '' : ' off'}`}
          onClick={() =>
            setLevelAllow(prod.uid, att.name, level.value, !isLevelAllowed)
          }
        >
          <ToggleSwitch className="mini" on={isLevelAllowed}>
            {level.label}
          </ToggleSwitch>
        </div>
      )
    })
  } else if (att.type === 'multiselect') {
    let nbSelected = 0
    const yesValue = att.yesValue || 1
    const noValue = att.noValue || 0

    content = att.options.map((option) => {
      const playValue = currentPlay[option.name] ?? att.defaultValue
      const baseValue = currentBase[option.name] ?? att.defaultValue

      if (playValue !== baseValue) {
        dirty = true
      }

      const selected = playValue === yesValue
      if (selected) nbSelected++

      const isLevelAllowed = true // Placeholder for optimization mode

      return !allowOptimize || isAttLocked ? (
        <div className="level-container" key={`${att.name}:${option.name}`}>
          <div
            className={'att-level' + (selected ? ' sel' : '')}
            onClick={() =>
              setPlay(prod.uid, option.name, selected ? noValue : yesValue)
            }
          >
            {option.label}
          </div>
        </div>
      ) : (
        <div
          className={'att-level with-toggle' + (isLevelAllowed ? '' : ' off')}
          onClick={() =>
            setLevelAllow(
              prod.uid,
              option.name,
              option.value ?? 1,
              !isLevelAllowed
            )
          }
          key={`${att.name}:${option.name}`}
        >
          <ToggleSwitch className="mini" on={isLevelAllowed}>
            Allow: {option.label}
          </ToggleSwitch>
        </div>
      )
    })

    if (att.maxSelect !== null && nbSelected > att.maxSelect) {
      error = `Too many options are selected. Select no more than ${att.maxSelect}.`
    }
  } else if (att.type === 'contin') {
    const playValue = currentPlay[att.name] ?? att.defaultValue
    const baseValue = currentBase[att.name] ?? att.defaultValue

    if (playValue !== baseValue) {
      dirty = true
    }

    content = (
      <div key={att.name} className="contin-area">
        {!allowOptimize || isAttLocked ? (
          <div>
            <ContinBox
              key={prod.uid + ':' + att.name}
              att={att}
              slot={prod}
              playValue={playValue}
              baseValue={baseValue}
              onChange={(newValue) => setPlay(prod.uid, att.name, newValue)}
            />
          </div>
        ) : (
          <div className="contin-range">
            Min:{' '}
            <input type="text" className="rangebox" defaultValue={att.min} />
            &nbsp Max:{' '}
            <input type="text" className="rangebox" defaultValue={att.max} />
          </div>
        )}
      </div>
    )
  } else {
    content = 'att type not supported'
  }

  const handleDirtyBitClick = () => {
    if (att.type === 'multiselect') {
      att.options.forEach((option) => {
        const baseValue = currentBase[option.name] ?? att.defaultValue
        setPlay(prod.uid, option.name, baseValue)
      })
    } else {
      const baseValue = currentBase[att.name] ?? att.defaultValue
      setPlay(prod.uid, att.name, baseValue)
    }
  }

  const multiSelectMessage =
    att.type === 'multiselect'
      ? att.maxSelect
        ? `(Select up to ${att.maxSelect})`
        : '(Select multiple)'
      : null

  const lightBlue = monitor_atts?.includes(att.name)
    ? { backgroundColor: 'rgba(66,133,250, 0.05)', position: 'relative' }
    : null

  return (
    <div
      className={'attribute-box' + (error ? ' has-error' : '')}
      style={lightBlue}
    >
      {monitor_atts?.includes(att.name) && (
        <div
          style={{
            position: 'absolute',
            right: 10,
            top: 10,
            fontSize: '5pt',
            color: '#808080',
          }}
        >
          <span>IMPACTS MODEL LEVEL</span>
        </div>
      )}
      <div
        className={'title ' + (isAttLocked ? 'locked' : 'unlocked')}
        onClick={() => {
          if (allowOptimize) {
            dispatch(
              optimActionCreators.setAttLock(
                prod.uid,
                att.name,
                !isAttLocked,
                currentPlay[att.name] ?? att.defaultValue
              )
            )
          }
        }}
      >
        <label title={error}>
          {att.label}
          {multiSelectMessage && (
            <span className="multi-select-message">{multiSelectMessage}</span>
          )}
          {/* {dirty && !allowOptimize && (
            <span
              className="dirty-bit"
              onClick={(e) => {
                e.stopPropagation()
                handleDirtyBitClick()
              }}
            >
              <i className="fas fa-circle" />
            </span>
          )} */}
        </label>
        {error && (
          <div>
            <span className="error-bit">
              <span className="glyph">
                <i className="fas fa-exclamation-triangle" />
              </span>
              <span className="message">{error}</span>
            </span>
          </div>
        )}
      </div>
      <div
        className={allowOptimize ? 'opt-level-container' : 'level-container'}
      >
        {content}
      </div>
    </div>
  )
}
